import { Button, Card, Divider, Paper } from '@mantine/core';
import Link from 'next/link';
import router from 'next/router';
import { useMemo } from 'react';
import { HeadTitle } from '~/components/common/HeadTitle';
import GreetingMessage from '~/components/home/GreetingMessage';
import ProjectTest from '~/components/testRun/ProjectTest';
import ProjectTestRun from '~/components/testRun/ProjectTestRun';
import { useCreateAndNavToTest } from '~/hooks/api/useCreateAndNavToTest';
import { queryParamToString } from '~/logic/query-param';
import { api } from '~/trpc-extra/api';

export default function HomePage() {
  const projectId = queryParamToString(router.query.projectId);
  const { data: projectName } = api.project.getProjectName.useQuery(
    { projectId },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
  const { data: tests, isLoading: isTestsLoading } = api.test.listTests.useQuery({ projectId });
  const { data: testSuites, isLoading: isTestSuitesLoading } = api.testSuite.list.useQuery({ projectId });
  const createTestHandler = useCreateAndNavToTest({});

  const onboardingCards = useMemo(
    () => [
      {
        title: 'Create a test',
        description: 'Click to add your first test',
        icon: '01',
        done: tests && tests?.length > 0,
        onClick: createTestHandler,
      },
      {
        title: 'Create a test suite',
        description: 'Trigger tests in bulk with ease',
        icon: '02',
        done: testSuites && testSuites.length > 0,
        onClick: () => router.push(`/project/${projectId}/testSuite`).catch(() => undefined),
      },
    ],
    [tests, testSuites, createTestHandler, projectId],
  );

  return (
    <>
      <HeadTitle title={projectName} />
      <div className="flex flex-col py-4">
        <div className="px-8">
          <GreetingMessage />
          <div className="pt-2 text-sm font-medium text-gray-600 dark:text-gray-500">
            Welcome back to your testing portal
          </div>
        </div>
        <div className="px-8">
          {!isTestsLoading &&
            !isTestSuitesLoading &&
            // Only show if there are things that haven't been done yet
            onboardingCards.reduce((prev, cur) => prev + (cur.done ? 0 : 1), 0) > 0 && (
              <div className="grid auto-rows-fr grid-cols-1 gap-4 pt-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-5">
                {onboardingCards.map((card) => (
                  <Paper
                    id={card.icon}
                    key={card.title}
                    title={card.title}
                    shadow="xs"
                    className={`onboard-card border border-gray-200 px-8 py-5 hover:cursor-pointer dark:border-neutral-600 ${
                      card.done ? 'border-b-2 border-b-blue-200 hover:border-b-0 dark:border-b-blue-700' : ''
                    }`}
                    onClick={card.onClick}
                  >
                    <div className="flex">
                      <div
                        className={`h-10 w-10 border-2 ${
                          card.done ? 'border-blue-500' : 'border-gray-300 dark:border-neutral-600'
                        } flex flex-shrink-0 items-center justify-center rounded-full`}
                      >
                        <span className={`${card.done ? 'text-blue-500' : ''} text-sm font-medium`}>{card.icon}</span>
                      </div>
                      <div className="flex-grow pl-4">
                        <div
                          className={`${
                            card.done ? 'text-blue-500' : 'text-gray-500 dark:text-neutral-300'
                          } text-sm font-bold`}
                        >
                          {card.title}
                        </div>
                        <div className="pt-1 text-sm text-gray-600 dark:text-neutral-400">{card.description}</div>
                      </div>
                    </div>
                  </Paper>
                ))}
              </div>
            )}
        </div>

        <div className="py-6">
          <Divider my="lg" />
        </div>

        {/* <div className="px-12">
        <RepoStatus />
      </div>
      <div className="py-6">
        <Divider my="lg" />
      </div> */}
        <div className="grid gap-y-6 px-10 xl:grid-cols-5 xl:gap-x-8">
          <Card
            className="xl:col-span-3"
            withBorder
            shadow="sm"
            radius="md"
          >
            <div className="px-6 pt-4 pb-8">
              <div className="flex items-center gap-2">
                <div className="text-md font-bold text-stone-600 dark:text-stone-400">Recent test runs </div>
                <Button
                  variant="subtle"
                  className="text-md px-3"
                  component={Link}
                  href={`/project/${projectId}/history`}
                >
                  View all
                </Button>
              </div>
              <div className="mt-6 flex h-full flex-col">
                <ProjectTestRun
                  defaultLimit={8}
                  showViewMoreButton={false}
                  showTableHeader={false}
                  showPagination={false}
                  noteMode="icon"
                />
              </div>
            </div>
          </Card>

          <Card
            withBorder
            shadow="sm"
            radius="md"
            className="xl:col-span-2"
          >
            <div className="px-8 pt-4 pb-12">
              <div className="flex items-center gap-2">
                <div className="text-md font-bold text-stone-600 dark:text-stone-400">Recently added tests </div>
                <Button
                  variant="subtle"
                  className="text-md px-3"
                  component={Link}
                  href={`/project/${projectId}/test`}
                >
                  View all
                </Button>
              </div>
              <div className="mt-6">
                <ProjectTest defaultLimit={8} />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
